import clsx from 'clsx'
import React, { Children, cloneElement, isValidElement, useRef } from 'react'
import type { PropsWithChildren } from 'react'
import Checkbox from '../Checkbox/Checkbox'
import { useForwardClick } from '../hooks/useForwardClick'
import { Directions } from '../utilities/directions'
import type { ColumnWidth } from './TableContext'
import { useTable } from './TableContext'

export const getWidthStyle = (width?: ColumnWidth): { width?: string } => {
  if (width === undefined || width === null || isNaN(Number(width))) {
    return {}
  }

  return { width: `${width}%` }
}

interface ColumnHeaderProps {
  /**
   * Used for column filters
   */
  'data-key'?: string
  /**
   * If true, reduces vertical padding for a more compact appearance
   * @default false
   */
  dense?: boolean
  /**
   * The sort direction for the column
   */
  sort?: Directions.Ascending | Directions.Descending
  /**
   * Whether the column header should stick to the right side of the table.
   * Used for columns that contain row actions or checkboxes to select rows
   * @default false
   */
  sticky?: boolean
  /**
   * Tab order index for keyboard navigation
   * @default -1
   */
  tabIndex?: number
  /**
   * The width of the column supporting a percentage value.
   * If not provided, the column will size automatically based on its content.
   */
  width?: ColumnWidth
}

function ColumnHeader({
  'data-key': key,
  dense = false,
  children,
  sort,
  sticky = false,
  tabIndex = -1,
  width,
}: PropsWithChildren<ColumnHeaderProps>): JSX.Element {
  const ref = useRef<HTMLTableHeaderCellElement | null>(null)
  const [state] = useTable()
  const buttonActions = useForwardClick(ref, 'button')
  const checkboxActions = useForwardClick(ref, 'input[type="checkbox"]')
  // @ts-expect-error The type property will exist on any ReactElement that is passed as a child, but will not
  // exist on any other object. Typescript however, will not obey `hasOwnProperty` checks, so we need to
  // ignore this error until we can determine a better interface
  const checkable = children && children.type === Checkbox

  const shouldBeSticky =
    sticky &&
    (state.columns.unselected.length > 0 ||
      Number(state.rowActions?.length) > 0)

  return (
    <th
      aria-sort={
        sort === Directions.Ascending
          ? 'ascending'
          : sort === Directions.Descending
          ? 'descending'
          : undefined
      }
      className={clsx(
        'px-4 text-left border-b border-r last:border-r-0 focus:outline-none last:px-4.5 last:w-[.1%] bg-black-5-hex',
        dense ? null : state.dense ? 'py-1.5' : 'py-3',
        checkable ? 'pl-3.5 pr-1.5' : null,
        state.selectable ? 'first:w-[.1%] first:border-r-0' : 'first:pl-6',
        shouldBeSticky
          ? 'sticky right-0 sticky-column overflow-visible border-black-10-hex hover:bg-black-10-hex focus:bg-black-10-hex active:bg-black-15-hex'
          : 'border-black-10 hover:bg-black-10-hex focus:bg-black-10-hex active:bg-black-15-hex',
      )}
      data-key={key}
      onClick={
        checkable ? checkboxActions.handleClick : buttonActions.handleClick
      }
      onKeyDown={
        checkable ? checkboxActions.handleKeyDown : buttonActions.handleKeyDown
      }
      ref={ref}
      // Can't put into className as it contains a dynamic arbitrary variable that tailwind
      // can't process.
      style={getWidthStyle(width)}
      tabIndex={tabIndex}
    >
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // @ts-expect-error The only current use case here is for the Table component where
          // we nest the `Menu`, `AddColumn` and `Checkbox.Input` components inside of the
          // `ColumnHeader`. While the `Checkbox.Input` component does not accept a
          // `focusRef`, the other two do. React will silently discard this prop
          // when it's not appropriate, but the types required for Typescript
          // to not complain are completely internal to that file, so we
          // will be ignoring this error.
          //
          // @see file://./Head.tsx
          return cloneElement(child, { focusRef: ref })
        }

        return child
      })}
    </th>
  )
}

export default ColumnHeader
